import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOverviewStore } from '@/stores/overviewStore';
import type { OverviewStep } from '@/stores/overviewStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { ZoomOverview } from '@/components/report/ZoomOverview';
import { ReportChat } from '@/components/report/ReportChat';
import { AlertCircle, RefreshCw, Check, Circle, Loader2, MessageSquare, Plug } from 'lucide-react';
import type { ZoomOverviewData } from '@/components/report/overview-types';

// =============================================================================
// Progress Panel (rendered inside the right sidebar)
// =============================================================================

function ProgressPanel({
  steps,
  progress,
}: {
  steps: OverviewStep[];
  progress: { completed: number; total: number };
}) {
  const pct = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;

  return (
    <>
      {/* Header */}
      <div className="h-14 flex items-center px-4 border-b border-border shrink-0">
        <span className="text-sm font-medium text-foreground">Loading Report</span>
      </div>

      {/* Progress content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">Fetching account data</span>
            <span className="text-xs text-muted-foreground">
              {progress.completed}/{progress.total}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Step list */}
        <div className="space-y-1">
          {steps.map((step) => (
            <StepRow key={step.name} step={step} />
          ))}
        </div>
      </div>
    </>
  );
}

function StepRow({ step }: { step: OverviewStep }) {
  return (
    <div className="flex items-center gap-2.5 py-0.5">
      <StepIcon status={step.status} />
      <span
        className={`text-xs ${
          step.status === 'active'
            ? 'text-primary font-medium'
            : step.status === 'done'
              ? 'text-foreground'
              : step.status === 'error'
                ? 'text-red-400'
                : 'text-muted-foreground'
        }`}
      >
        {step.status === 'active' ? `Fetching ${step.label.toLowerCase()}...` : step.label}
      </span>
    </div>
  );
}

function StepIcon({ status }: { status: OverviewStep['status'] }) {
  switch (status) {
    case 'done':
      return (
        <div className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/20">
          <Check className="h-2.5 w-2.5 text-primary" />
        </div>
      );
    case 'active':
      return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
    case 'error':
      return (
        <div className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500/20">
          <AlertCircle className="h-2.5 w-2.5 text-red-400" />
        </div>
      );
    default:
      return <Circle className="h-4 w-4 text-muted-foreground/40" />;
  }
}

// =============================================================================
// Main Page
// =============================================================================

export function ReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { status, data, steps, progress, error, fetchOverview, loadStored, silentRefresh, clear } = useOverviewStore();
  const { enrichedAccounts, isLoadingEnriched, fetchEnrichedAccounts, checkConnection, getZoomConnectUrl } = useConnectionStore();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [chatWidth, setChatWidth] = useState(320);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Handle OAuth callback query params (oauth=success or oauth=error)
  useEffect(() => {
    const oauthResult = searchParams.get('oauth');
    if (oauthResult) {
      setSearchParams({}, { replace: true });
      fetchEnrichedAccounts();
    }
  }, [searchParams, setSearchParams, fetchEnrichedAccounts]);

  // Fetch enriched accounts on mount
  useEffect(() => {
    fetchEnrichedAccounts();
    checkConnection();
  }, [fetchEnrichedAccounts, checkConnection]);

  // Single-account product: no picker. As soon as the connected account is
  // known, serve the STORED report instantly; fall back to a full streaming
  // generation only when nothing is stored yet, and refresh silently in the
  // background when the stored copy is flagged stale (e.g. a fix ran).
  useEffect(() => {
    const account = enrichedAccounts[0] ?? null;
    if (!account || isLoadingEnriched || selectedAccountId) return;
    // Always adopt the account on (re)mount so the right branch can render.
    setSelectedAccountId(account.accountId);
    // If the global store already holds a rendered report (e.g. returning from
    // the write-enablement chat), adopt it as-is — don't blank it or refetch;
    // ZoomOverview's resume effect handles any queued fix + silent refresh.
    if (status === 'ready' && data) return;
    // If a stream is already in flight, let it finish.
    if (status === 'streaming') return;
    void (async () => {
      const stored = await loadStored();
      if (!stored.found) {
        fetchOverview(account.accountId, 'initial');
      } else if (stored.stale) {
        void silentRefresh(account.accountId);
      }
    })();
  }, [isLoadingEnriched, enrichedAccounts, selectedAccountId, status, data, loadStored, fetchOverview, silentRefresh]);

  // Manual refresh — explicit user intent, so the full streaming UX (with the
  // progress sidebar) is the right feedback.
  const handleRefresh = () => {
    if (!selectedAccountId) {
      fetchEnrichedAccounts();
      return;
    }
    clear();
    fetchOverview(selectedAccountId);
  };

  const handleConnect = useCallback(async () => {
    const url = await getZoomConnectUrl('/report');
    if (url) {
      window.location.href = url;
    }
  }, [getZoomConnectUrl]);

  const noAccounts = !isLoadingEnriched && enrichedAccounts.length === 0;
  const showSidebar = !!selectedAccountId && (status === 'streaming' || status === 'ready');
  const sidebarReady = status === 'ready';
  // Terminal content is visible when one of the branches below renders. Until
  // then (accounts still resolving, auto-select pending, fetch not yet kicked
  // off — e.g. right after redirect back from write-enablement) show a loader
  // instead of a blank screen.
  const hasReportContent =
    (status === 'streaming' && !!selectedAccountId && !!data && hasMinimumData(data)) ||
    (status === 'ready' && !!data && !!selectedAccountId) ||
    (status === 'error' && !!selectedAccountId);
  const showLoading = !noAccounts && !hasReportContent;

  return (
    <div className="flex flex-1 flex-col bg-background text-foreground overflow-hidden">
      <main className="flex flex-1 overflow-hidden">
        {/* Main content area */}
        <div className={`flex-1 overflow-y-auto ${showSidebar ? 'lg:pr-80' : ''}`}>
          {/* No Zoom account connected yet */}
          {noAccounts && (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
              <div className="rounded-full bg-primary/10 p-4">
                <Plug className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Connect your Zoom account</h2>
              <p className="max-w-md text-sm text-muted-foreground">
                ZAC needs a connected Zoom account to generate your report.
              </p>
              <button
                onClick={handleConnect}
                className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Connect Zoom
              </button>
            </div>
          )}

          {/* Loading — accounts resolving / report about to stream (also the
              window right after returning from write-enablement). */}
          {showLoading && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading your report…</p>
            </div>
          )}

          {/* Streaming — show partial data as it arrives */}
          {status === 'streaming' && selectedAccountId && data && hasMinimumData(data) && (
            <div className="animate-fade-in">
              <ZoomOverview
                data={data as ZoomOverviewData}
                onRefresh={handleRefresh}
                isStreaming
                accountId={selectedAccountId ?? undefined}
              />
            </div>
          )}

          {/* Ready */}
          {status === 'ready' && data && selectedAccountId && (
            <ZoomOverview
              data={data as ZoomOverviewData}
              onRefresh={handleRefresh}
              accountId={selectedAccountId ?? undefined}
            />
          )}

          {/* Error */}
          {status === 'error' && selectedAccountId && (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4 sm:p-6 md:p-8 text-center">
              <div className="rounded-full bg-red-500/10 p-4">
                <AlertCircle className="h-10 w-10 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Failed to Load Overview</h2>
              <p className="max-w-md text-sm text-muted-foreground">{error}</p>
              <div className="mt-2">
                <button
                  onClick={handleRefresh}
                  className="flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar — desktop only, fixed to viewport right edge */}
        {showSidebar && (
          <div className="hidden lg:block fixed top-0 right-0 bottom-0 z-30" style={{ width: chatWidth }}>
            {/* Progress panel — fades out */}
            <div
              className={`absolute inset-0 flex flex-col bg-card border-l border-border transition-all duration-500 ease-in-out ${
                sidebarReady
                  ? 'opacity-0 -translate-y-3 pointer-events-none'
                  : 'opacity-100 translate-y-0'
              }`}
            >
              <ProgressPanel steps={steps} progress={progress} />
            </div>

            {/* Chat panel — fades in, grows leftward to overlap main content */}
            <div
              className={`absolute top-0 bottom-0 right-0 transition-all duration-500 ease-in-out ${
                sidebarReady
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-3 pointer-events-none'
              }`}
              style={{ width: chatWidth }}
            >
              <ReportChat data={data as ZoomOverviewData} accountId={selectedAccountId ?? undefined} width={chatWidth} onWidthChange={setChatWidth} />
            </div>
          </div>
        )}

        {/* Mobile chat overlay */}
        {showSidebar && sidebarReady && isChatOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setIsChatOpen(false)} />
            <div className="relative h-full">
              <ReportChat data={data as ZoomOverviewData} accountId={selectedAccountId ?? undefined} onClose={() => setIsChatOpen(false)} />
            </div>
          </div>
        )}

        {/* Floating chat button — mobile only */}
        {showSidebar && sidebarReady && !isChatOpen && (
          <button
            onClick={() => setIsChatOpen(true)}
            className="fixed bottom-6 right-6 z-30 lg:hidden flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
            aria-label="Open chat"
          >
            <MessageSquare className="h-6 w-6" />
          </button>
        )}
      </main>
    </div>
  );
}

/** Show partial overview once we have at least account data */
function hasMinimumData(data: Partial<ZoomOverviewData>): boolean {
  return !!data.account;
}
